/*
 * What the Spore analysis workbench works out from the spores of its mask
 * (detect.js analyseMask): micrometres, the filters, the summary, the
 * histogram bins and the exports. Pure functions, no React, no DOM.
 *
 * A candidate comes from analyseMask in IMAGE pixels. withScale() adds
 * length_um, width_um and area_um2 (x um_per_px, x um_per_px^2), null when the
 * slide has no scale, and Q = length / width. The size filter works in
 * micrometres squared when there is a scale and in pixels squared otherwise
 * (`unit`), so the same numbers are shown and filtered.
 *
 * Each spore also gets its surface area and volume as an ellipsoid of
 * revolution (ellipsoid()): length L along its long axis, width W across, and
 * a depth equal to W -- a spore seen from above shows two of its three axes,
 * and the hidden one is taken as the width, the usual assumption for
 * ellipsoid and amygdaliform spores. Q = L / W.
 *
 * The shape filter is the ELLIPSE FIT (shape_fit): the spore's circularity
 * 4piA/P^2 over the circularity of an ellipse of its own length and width
 * (ellipseCircularity, Ramanujan's perimeter), so 1 is a perfect ellipse at
 * any elongation -- circularity alone scores a clean spore of Q 3.5 under 0.6
 * -- while a clump of two touching spores scores about 0.7 and ragged debris
 * less. Measured outlines of ideal ellipses score 0.91-0.96.
 *
 * Spores come from two places: the mask's components (`source: 'auto'`) and
 * the manual tools (manualSpores(): a spore mark's length and width lines, or
 * a counted point). combineSpores() puts them together once each.
 */
import { csvCell } from '../importTemplate';
import { pointInPolygon } from '../annotationGeometry';

const round3 = v => Math.round(v * 1000) / 1000;
const finite = v => v !== null && v !== undefined && Number.isFinite(v);

/**
 * Surface area and volume of the ellipsoid with axes L, W and W: a prolate
 * spheroid (a = L/2 > b = W/2), an oblate one when the width is the longer,
 * or a sphere. In the units of L and W (squared, cubed); nulls when either is
 * missing or not positive.
 *
 *   V = 4/3 pi a b^2 = pi L W^2 / 6
 *   S = 2 pi b^2 (1 + a / (b e) asin e),          e = sqrt(1 - b^2/a^2)  (prolate)
 *   S = 2 pi b^2 (1 + (1 - e^2) / e atanh e),      e = sqrt(1 - a^2/b^2)  (oblate)
 *   S = 4 pi a^2                                                           (sphere)
 */
export function ellipsoid(length, width) {
  if (!finite(length) || !finite(width) || length <= 0 || width <= 0) return { surface: null, volume: null };
  const a = length / 2;
  const b = width / 2;
  const volume = (4 / 3) * Math.PI * a * b * b;
  let surface;
  if (Math.abs(a - b) <= 1e-9 * Math.max(a, b)) {
    surface = 4 * Math.PI * a * a;
  } else if (a > b) {
    const e = Math.sqrt(1 - (b * b) / (a * a));
    surface = 2 * Math.PI * b * b * (1 + (a / (b * e)) * Math.asin(e));
  } else {
    const e = Math.sqrt(1 - (a * a) / (b * b));
    surface = 2 * Math.PI * b * b * (1 + ((1 - e * e) / e) * Math.atanh(e));
  }
  return { surface, volume };
}

const orNull = (v, f) => (finite(v) ? f(v) : null);

/** The circularity 4piA/P^2 of an ellipse of this length and width (Ramanujan's perimeter); null when either is missing. */
export function ellipseCircularity(length, width) {
  if (!finite(length) || !finite(width) || length <= 0 || width <= 0) return null;
  const a = length / 2;
  const b = width / 2;
  const perimeter = Math.PI * (3 * (a + b) - Math.sqrt((3 * a + b) * (a + 3 * b)));
  return Math.min(1, (4 * Math.PI * Math.PI * a * b) / (perimeter * perimeter));
}

/** The spores with their micrometre fields, Q, and ellipsoid surface and volume. */
export function withScale(spores, umPerPx) {
  const scale = umPerPx ? Number(umPerPx) : null;
  return (spores || []).map((s) => {
    const q = s.width_px > 0 && finite(s.length_px) ? round3(s.length_px / s.width_px) : null;
    const px = ellipsoid(s.length_px, s.width_px);
    const ideal = ellipseCircularity(s.length_px, s.width_px);
    const out = {
      ...s,
      source: s.source || 'auto',
      q,
      shape_fit: finite(s.circularity) && ideal ? round3(s.circularity / ideal) : null,
      surface_px2: orNull(px.surface, round3),
      volume_px3: orNull(px.volume, round3),
    };
    if (!scale) return { ...out, length_um: null, width_um: null, area_um2: null, surface_um2: null, volume_um3: null };
    return {
      ...out,
      length_um: orNull(s.length_px, v => round3(v * scale)),
      width_um: orNull(s.width_px, v => round3(v * scale)),
      area_um2: orNull(s.area_px, v => round3(v * scale * scale)),
      surface_um2: orNull(px.surface, v => round3(v * scale * scale)),
      volume_um3: orNull(px.volume, v => round3(v * scale * scale * scale)),
    };
  });
}

/** The unit the size filter speaks: 'um' with a scale, 'px' without. */
export const unitFor = umPerPx => (umPerPx ? 'um' : 'px');

export const lengthOf = (s, unit) => (unit === 'um' ? s.length_um : s.length_px);
export const widthOf = (s, unit) => (unit === 'um' ? s.width_um : s.width_px);
export const areaOf = (s, unit) => (unit === 'um' ? s.area_um2 : s.area_px);
export const surfaceOf = (s, unit) => (unit === 'um' ? s.surface_um2 : s.surface_px2);
export const volumeOf = (s, unit) => (unit === 'um' ? s.volume_um3 : s.volume_px3);

/** The p-th percentile (0..1) of `values`, linearly interpolated. */
export function percentile(values, p) {
  const sorted = [...values].filter(v => v !== null && v !== undefined && !Number.isNaN(v)).sort((a, b) => a - b);
  if (!sorted.length) return null;
  const pos = (sorted.length - 1) * Math.min(1, Math.max(0, p));
  const lo = Math.floor(pos);
  const hi = Math.min(lo + 1, sorted.length - 1);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
}

//: no fungal spore is smaller than this (the smallest are about 2 x 1.5 um): the size filter starts above it
export const MIN_SPORE_AREA_UM2 = 2;

/**
 * The size filter's starting bounds: the 2nd and 98th percentiles of the
 * areas, by NEAREST RANK (the area of an actual candidate, rounded outwards),
 * so a small set (under ~50) keeps every candidate and only a large one has
 * its extreme tails trimmed -- an interpolated percentile would always cut
 * the smallest and the largest spore off. Areas under `floor` (dust: at the
 * camera's full resolution a speck of a few pixels is a candidate) are left
 * out first and the minimum never starts under it.
 */
export function defaultAreaBounds(spores, unit, floor = 0) {
  const areas = spores.map(s => areaOf(s, unit)).filter(v => v !== null && v !== undefined && v >= floor)
    .sort((a, b) => a - b);
  if (!areas.length) return { areaMin: floor, areaMax: null };
  const n = areas.length - 1;
  return { areaMin: Math.max(floor, Math.floor(areas[Math.floor(0.02 * n)] * 100) / 100),
           areaMax: Math.ceil(areas[Math.ceil(0.98 * n)] * 100) / 100 };
}

/** Does this candidate pass the filters? (The manual exclusion is the caller's.) */
export function passes(s, { fitMin = 0, circularityMin = 0, areaMin = null, areaMax = null, unit = 'px' } = {}) {
  if (s.circularity < circularityMin) return false;
  if (finite(s.shape_fit) && s.shape_fit < fitMin) return false;
  const area = areaOf(s, unit);
  if (areaMin !== null && areaMin !== undefined && area < areaMin) return false;
  if (areaMax !== null && areaMax !== undefined && area > areaMax) return false;
  return true;
}

/** Every candidate flagged: passes (the filters) and included (passes and not excluded by hand). */
export function applyFilters(spores, params, excluded = new Set()) {
  return spores.map((s) => {
    const ok = passes(s, params);
    return { ...s, passes: ok, included: ok && !excluded.has(s.idx) };
  });
}

function stats(values) {
  const v = values.filter(x => x !== null && x !== undefined && !Number.isNaN(x));
  if (!v.length) return { n: 0, mean: null, sd: null, min: null, max: null };
  const mean = v.reduce((a, b) => a + b, 0) / v.length;
  const sd = v.length > 1 ? Math.sqrt(v.reduce((a, b) => a + (b - mean) ** 2, 0) / (v.length - 1)) : null;
  return { n: v.length, mean: round3(mean), sd: sd === null ? null : round3(sd),
           min: round3(Math.min(...v)), max: round3(Math.max(...v)) };
}

/**
 * The summary over `spores`: N (every spore, a counted-only one too), how many
 * have both a length and a width (`measured`), the total projected area, and
 * mean / sd (sample) / min / max of length, width, projected area, Q,
 * ellipsoid surface and volume -- each over the spores that have it.
 */
export function summarise(spores, unit = 'px') {
  const list = spores || [];
  const areas = list.map(s => areaOf(s, unit)).filter(finite);
  return {
    count: list.length,
    measured: list.filter(s => finite(lengthOf(s, unit)) && finite(widthOf(s, unit))).length,
    unit,
    totalArea: areas.length ? round3(areas.reduce((a, b) => a + b, 0)) : 0,
    length: stats(list.map(s => lengthOf(s, unit))),
    width: stats(list.map(s => widthOf(s, unit))),
    area: stats(areas),
    q: stats(list.map(s => s.q)),
    surface: stats(list.map(s => surfaceOf(s, unit))),
    volume: stats(list.map(s => volumeOf(s, unit))),
  };
}

const UNIT_TEXT = { um: 'µm', px: 'px' };
const fixed = (v, digits) => Number(v).toFixed(digits);

/**
 * The mycological notation of a summary:
 *   L × W = (Lmin–Lmax) × (Wmin–Wmax) µm, Q_avg = 1.62, n = 42
 * lengths to 0.1, Q to 0.01, n the spores measured both ways. Null when no
 * spore has a length and a width.
 */
export function mycologicalNotation(summary) {
  if (!summary || !summary.measured || !finite(summary.length.min) || !finite(summary.width.min)) return null;
  const u = UNIT_TEXT[summary.unit] || summary.unit;
  const L = summary.length;
  const W = summary.width;
  return `L × W = (${fixed(L.min, 1)}–${fixed(L.max, 1)}) × (${fixed(W.min, 1)}–${fixed(W.max, 1)}) ${u}, `
    + `Q_avg = ${fixed(summary.q.mean, 2)}, n = ${summary.measured}`;
}

/** The means: "L = 9.5 ± 0.8 µm, W = 5.6 ± 0.4 µm" (the sd is "–" for a single spore). */
export function meanNotation(summary) {
  if (!summary || !summary.measured || !finite(summary.length.mean) || !finite(summary.width.mean)) return null;
  const u = UNIT_TEXT[summary.unit] || summary.unit;
  const sd = st => (finite(st.sd) ? fixed(st.sd, 1) : '–');
  return `L = ${fixed(summary.length.mean, 1)} ± ${sd(summary.length)} ${u}, `
    + `W = ${fixed(summary.width.mean, 1)} ± ${sd(summary.width)} ${u}`;
}

// ── manual spores, and the two together ───────────────────────────────────
const lineLength = points => Math.hypot(points[1][0] - points[0][0], points[1][1] - points[0][1]);

/**
 * Where a manual mark stands: a point's point; a spore drawn as lines, the
 * middle of its length line (else of its first line); a hypha, its first point.
 */
export function markAnchor(mark) {
  const shapes = mark?.shapes || [];
  if (!shapes.length) return null;
  const line = shapes.find(s => s.kind === 'line' && s.role === 'length') || shapes.find(s => s.kind === 'line');
  if (line) {
    const [a, b] = line.points;
    return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  }
  const first = shapes[0].points || [];
  return first.length ? first[0] : null;
}

/**
 * The spores drawn by hand: every mark of feature 'spore'. Its length line
 * (the longest, if several) is L and its width line W; a point mark is a
 * spore counted but not measured. Numbered from `firstIdx`, in image pixels,
 * `source: 'manual'`, with `mark` the mark's position in `marks`.
 */
export function manualSpores(marks, firstIdx = 1) {
  const out = [];
  (marks || []).forEach((mark, i) => {
    if (mark.feature !== 'spore') return;
    const anchor = markAnchor(mark);
    if (!anchor) return;
    const longest = role => (mark.shapes || []).filter(sh => sh.kind === 'line' && sh.role === role)
      .map(sh => lineLength(sh.points)).reduce((m, v) => (m === null || v > m ? v : m), null);
    const length = longest('length');
    const width = longest('width');
    out.push({
      idx: firstIdx + out.length, source: 'manual', mark: i,
      centroid_px: [round3(anchor[0]), round3(anchor[1])],
      length_px: length === null ? null : round3(length),
      width_px: width === null ? null : round3(width),
      area_px: null, circularity: null, bbox_px: null,
    });
  });
  return out;
}

/** Is p on this detected spore (inside its outline, else its bounding box)? */
export function onSpore(p, d) {
  if ((d.outline_px || []).length >= 3) return pointInPolygon(p, d.outline_px);
  const [x, y, w, h] = d.bbox_px || [];
  return finite(x) && p[0] >= x && p[0] <= x + w && p[1] >= y && p[1] <= y + h;
}

/**
 * The detected spores and the manual ones, each spore once: a hand-drawn
 * spore whose anchor lies on a detected one IS that spore. Measured by hand,
 * the hand measurement replaces the detection's; only counted (a point), the
 * detection's measurement stands. `detected` are the included ones.
 */
export function combineSpores(detected, manual) {
  const replaced = new Set();
  const kept = [];
  for (const m of manual || []) {
    const twin = (detected || []).find(d => !replaced.has(d.idx) && onSpore(m.centroid_px, d));
    if (twin && !finite(m.length_px)) continue;
    if (twin) replaced.add(twin.idx);
    kept.push(m);
  }
  return [...(detected || []).filter(d => !replaced.has(d.idx)), ...kept];
}

/** Round ticks over [low, high]: 1, 2, 2.5, 5 x a power of ten (stats_charts._nice_ticks). */
export function niceTicks(low, high, count = 6) {
  let hi = high;
  if (!(hi > low)) hi = low + 1;
  const raw = (hi - low) / count;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map(m => m * magnitude).find(s => s >= raw);
  const start = Math.floor(low / step) * step;
  const ticks = [];
  for (let v = start; v <= hi + step * 1e-9; v += step) ticks.push(Number(v.toFixed(10)));
  if (ticks[ticks.length - 1] < hi) ticks.push(Number((ticks[ticks.length - 1] + step).toFixed(10)));
  return ticks;
}

/**
 * Histogram bins between nice ticks: [{x0, x1, count}], plus the ticks and
 * the tallest bar. Empty when there is nothing to bin.
 */
export function histogram(values, { bins = 8 } = {}) {
  const v = values.filter(x => x !== null && x !== undefined && !Number.isNaN(x));
  if (!v.length) return { bins: [], ticks: [], max: 0 };
  const ticks = niceTicks(Math.min(...v), Math.max(...v), bins);
  const out = [];
  for (let i = 0; i + 1 < ticks.length; i += 1) out.push({ x0: ticks[i], x1: ticks[i + 1], count: 0 });
  const last = out.length - 1;
  for (const x of v) {
    let i = out.findIndex(b => x >= b.x0 && x < b.x1);
    if (i === -1) i = last;   // the maximum lands in the last bin
    out[i].count += 1;
  }
  return { bins: out, ticks, max: Math.max(...out.map(b => b.count)) };
}

// ── exports ─────────────────────────────────────────────────────────────────
const FORMULA_START = ['=', '+', '-', '@'];

/** A CSV cell: quoted when needed, and text that a spreadsheet would run neutralised. */
export function cell(value) {
  if (typeof value === 'string' && FORMULA_START.some(c => value.startsWith(c))) return csvCell(`'${value}`);
  return csvCell(value);
}

export const CSV_COLUMNS = ['idx', 'source', 'included', 'length_um', 'width_um', 'q', 'area_um2', 'surface_um2',
                            'volume_um3', 'circularity', 'shape_fit', 'length_px', 'width_px', 'area_px', 'surface_px2',
                            'volume_px3', 'perimeter_px', 'orientation_deg', 'centroid_x_px', 'centroid_y_px'];

/**
 * The spores and the summary as CSV text: a UTF-8 BOM (Excel reads Hebrew
 * headers that way), CRLF rows, csvCell quoting, formula guard.
 */
export function toCsv(spores, summary, { image = {}, umPerPx = null, notes = null, maskPixels = null } = {}) {
  const rows = [CSV_COLUMNS.join(',')];
  for (const s of spores) {
    rows.push([s.idx, s.source || 'auto', s.included === false ? 'no' : 'yes', s.length_um, s.width_um, s.q,
               s.area_um2, s.surface_um2, s.volume_um3, s.circularity, s.shape_fit, s.length_px, s.width_px, s.area_px,
               s.surface_px2, s.volume_px3, s.perimeter_px, s.orientation_deg,
               s.centroid_px?.[0], s.centroid_px?.[1]].map(cell).join(','));
  }
  rows.push('');
  rows.push(['summary', 'value'].join(','));
  const unit = summary.unit === 'um' ? 'um' : 'px';
  const put = (k, v) => rows.push([cell(k), cell(v)].join(','));
  put('image_id', image.image_id ?? '');
  put('um_per_px', umPerPx ?? '');
  if (maskPixels !== null) put('mask_pixels', maskPixels);
  put('count', summary.count);
  put('measured', summary.measured ?? summary.count);
  put(`total_area_${unit}2`, summary.totalArea);
  const SUFFIX = { q: '', area: `_${unit}2`, surface: `_${unit}2`, volume: `_${unit}3` };
  for (const name of ['length', 'width', 'area', 'q', 'surface', 'volume']) {
    const st = summary[name];
    if (!st) continue;
    const suffix = SUFFIX[name] ?? `_${unit}`;
    put(`mean_${name}${suffix}`, st.mean ?? '');
    put(`sd_${name}${suffix}`, st.sd ?? '');
    put(`min_${name}${suffix}`, st.min ?? '');
    put(`max_${name}${suffix}`, st.max ?? '');
  }
  const notation = mycologicalNotation(summary);
  if (notation) put('notation', notation);
  if (notes) put('notes', notes);
  // \uFEFF as an escape, never a raw character (utils/importTemplate.js says why).
  return `\uFEFF${rows.join('\r\n')}\r\n`;
}

/** The whole analysis as pretty JSON. */
export function toJson(spores, summary, { image = {}, umPerPx = null, params = {}, notes = null } = {}) {
  return JSON.stringify({
    image: { image_id: image.image_id ?? null, width_px: image.width_px ?? null, height_px: image.height_px ?? null },
    um_per_px: umPerPx,
    params,
    summary,
    notation: mycologicalNotation(summary),
    spores,
    notes: notes || null,
    exported_at: new Date().toISOString(),
  }, null, 2);
}

/**
 * What POST /microscopy/{id}/spore-analyses takes besides the mask: `included`,
 * the numbers of the mask's components kept (the server measures them itself),
 * and `spores`, the ones drawn by hand, with the micrometre fields (or the
 * pixel ones when there is no scale). The server works Q, the surface and the
 * volume out again.
 */
export function toSaved(spores, umPerPx) {
  return {
    included: spores.filter(s => (s.source || 'auto') === 'auto').map(s => s.idx).sort((a, b) => a - b),
    spores: toSavedRows(spores.filter(s => s.source === 'manual'), umPerPx),
  };
}

/** One stored row per spore (the CSV's own fields, as the server keeps them). */
export function toSavedRows(spores, umPerPx) {
  return spores.map((s) => {
    const row = { idx: s.idx, source: s.source || 'auto', circularity: s.circularity ?? null, q: s.q ?? null,
                  centroid_px: s.centroid_px, bbox_px: s.bbox_px ?? null,
                  length_px: s.length_px ?? null, width_px: s.width_px ?? null, area_px: s.area_px ?? null };
    if (umPerPx) {
      Object.assign(row, { length_um: s.length_um ?? null, width_um: s.width_um ?? null, area_um2: s.area_um2 ?? null });
    }
    return row;
  });
}
